import { useState, useEffect } from "react";
import { ArrowLeft, Save, Database, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

export default function RetentionSettings() {
  const [, navigate] = useLocation();
  const [photoRetentionMonths, setPhotoRetentionMonths] = useState<number | null>(null);
  const [enableColdStorage, setEnableColdStorage] = useState(false);
  const [coldStorageAfterMonths, setColdStorageAfterMonths] = useState<number | null>(null);
  const [allowDeletion, setAllowDeletion] = useState(false);

  const { data: settings, refetch } = trpc.system.getRetentionSettings.useQuery();
  const { data: stats } = trpc.system.getStorageStats.useQuery();
  const updateMutation = trpc.system.updateRetentionSettings.useMutation();

  // Load settings when data arrives
  useEffect(() => {
    if (settings) {
      setPhotoRetentionMonths(settings.photoRetentionMonths);
      setEnableColdStorage(settings.enableColdStorage);
      setColdStorageAfterMonths(settings.coldStorageAfterMonths);
      setAllowDeletion(settings.allowDeletion);
    }
  }, [settings]);

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({
        photoRetentionMonths,
        enableColdStorage,
        coldStorageAfterMonths,
        allowDeletion,
      });

      await refetch();
      toast.success("Retention settings updated successfully");
    } catch (error: any) {
      toast.error(error.message || "Failed to update settings");
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="container max-w-4xl py-8">
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
        <h1 className="text-3xl font-bold">Retention Settings</h1>
        <p className="text-muted-foreground mt-2">
          Configure photo archival policies and storage management
        </p>
      </div>

      {/* Storage Statistics */}
      {stats && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5" />
              Current Storage Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-2xl font-bold">{stats.totalPhotos.toLocaleString()}</div>
                <div className="text-sm text-muted-foreground">Total Photos</div>
              </div>
              <div>
                <div className="text-2xl font-bold">{formatBytes(stats.totalSizeBytes)}</div>
                <div className="text-sm text-muted-foreground">Total Size</div>
              </div>
              <div>
                <div className="text-sm font-medium">
                  {stats.oldestPhotoDate 
                    ? new Date(stats.oldestPhotoDate).toLocaleDateString()
                    : 'N/A'}
                </div>
                <div className="text-sm text-muted-foreground">Oldest Photo</div>
              </div>
              <div>
                <div className="text-sm font-medium">
                  {stats.newestPhotoDate 
                    ? new Date(stats.newestPhotoDate).toLocaleDateString()
                    : 'N/A'}
                </div>
                <div className="text-sm text-muted-foreground">Newest Photo</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Retention Policy */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Photo Retention Policy</CardTitle>
          <CardDescription>
            Configure how long photos are kept in the system
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="photoRetentionMonths">
              Photo Retention Period (months)
            </Label>
            <Input
              id="photoRetentionMonths"
              type="number"
              min="0"
              placeholder="Leave empty to keep forever"
              value={photoRetentionMonths ?? ""}
              onChange={(e) => setPhotoRetentionMonths(e.target.value ? parseInt(e.target.value) : null)}
            />
            <p className="text-sm text-muted-foreground">
              Leave empty to keep photos forever. Enter a number to automatically archive photos older than this period.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Cold Storage */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Cold Storage</CardTitle>
          <CardDescription>
            Move older photos to cold storage to reduce costs
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="enableColdStorage">Enable Cold Storage</Label>
              <p className="text-sm text-muted-foreground">
                Automatically move old photos to cold storage
              </p>
            </div>
            <Switch
              id="enableColdStorage"
              checked={enableColdStorage}
              onCheckedChange={setEnableColdStorage}
            />
          </div>

          {enableColdStorage && (
            <div className="space-y-2">
              <Label htmlFor="coldStorageAfterMonths">
                Move to Cold Storage After (months)
              </Label>
              <Input
                id="coldStorageAfterMonths"
                type="number"
                min="1"
                placeholder="e.g., 12"
                value={coldStorageAfterMonths ?? ""}
                onChange={(e) => setColdStorageAfterMonths(e.target.value ? parseInt(e.target.value) : null)}
              />
              <p className="text-sm text-muted-foreground">
                Photos older than this will be moved to cold storage (slower access, lower cost)
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Deletion Permission */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="w-5 h-5" />
            Deletion Permission
          </CardTitle>
          <CardDescription>
            Enable permanent deletion of photos (use with caution)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Warning</AlertTitle>
            <AlertDescription>
              Enabling deletion permission allows the system to permanently delete photos based on retention policies.
              This action cannot be undone. By default, photos are never deleted without explicit permission.
            </AlertDescription>
          </Alert>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="allowDeletion" className="text-destructive font-semibold">
                Allow Permanent Deletion
              </Label>
              <p className="text-sm text-muted-foreground">
                Photos exceeding retention period will be permanently deleted
              </p>
            </div>
            <Switch
              id="allowDeletion"
              checked={allowDeletion}
              onCheckedChange={setAllowDeletion}
            />
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={updateMutation.isPending}
          size="lg"
        >
          <Save className="w-4 h-4 mr-2" />
          Save Settings
        </Button>
      </div>
    </div>
  );
}
